import axios from 'axios';
import _ from 'lodash';
import { CloseWebView } from './webviewutils';
import formatText from './localizationUtils';
import errorHtmlFromTemplate from './errorHtmlFromTemplate';
import flowTypes from '../../helpers/constants/flowType';
import channelTypes from '../../helpers/constants/channelType';
import EnvConfig from '../../../config/client';
import getTitleLabelByTemplateId from '../../helpers/getTitleLabelByTemplateId';
import setupAxiosRetry from './setup-axios-retry';
import logger from './logger';
import { LoggingEvents } from './logger/loggingEvents';

setupAxiosRetry(axios);
/**
 * Submits the card data to the server, with no handling of the response
 * @param {Object} requestData
 * @param timeout
 * @param isTrustWidgetEnabled
 * @returns {Promise<AxiosPromise>}
 */
export const SubmitData = async (
  requestData,
  timeout,
  isTrustWidgetEnabled = false,
) => {
  const { jwtToken, isCGPEnabled } = requestData;
  const { origin } = window.location;

  if (
    window.trustApi &&
    typeof window.trustApi.getTrustPayload === 'function' &&
    isTrustWidgetEnabled
  ) {
    try {
      requestData.trustWidgetPayload = await window.trustApi.getTrustPayload();
    } catch (e) {
      logger.log('ERROR', LoggingEvents.PaymentWidgetLogs, {
        message: `getTrustPayload Failure: ${e.message}`,
      });
    }
  }

  _.omit(requestData, jwtToken);
  const AuthorizationHeader = 'x-cp-authorization';
  const request = {
    ...(isCGPEnabled && origin && { baseURL: `${origin}/vaccards` }),
    url: '/submit',
    method: 'post',
    data: requestData,
    timeout: timeout || EnvConfig.axios.defaultTimeout,
    headers: {
      'Content-Type': 'application/json',
      ...(jwtToken && { [AuthorizationHeader]: jwtToken }),
    },
  };
  const response = await axios.request(request);
  return response;
};

/**
 * Submits the travel alert form data to the server, with no handling of the response
 * @param {Object} requestData
 * @returns {Promise<AxiosPromise>}
 */
export const SubmitTravelDetails = async (requestData, timeout) => {
  const request = {
    url: '/submit-travel-details',
    method: 'post',
    data: requestData,
    timeout: timeout || EnvConfig.axios.defaultTimeout,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios.request(request);
  return response;
};

/**
 * Gets the eligibility details for customer travel data information from the server, with no handling of the response
 * @param {Object} requestData
 * @returns {Promise<AxiosPromise>}
 */
export const GetEligibilityDetails = async (requestData, timeout) => {
  const request = {
    url: '/get-travel-eligibility',
    method: 'post',
    data: requestData,
    timeout: timeout || EnvConfig.axios.defaultTimeout,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const response = await axios.request(request);
  return response;
};

/**
 * Publishes an event to the server, with no handling of the response
 * @param {Array} requestData The event message
 * @param {Number} timeout The read timeout in milliseconds
 * @returns {Promise<AxiosPromise>}
 */
export const PublishEvent = async (requestData, timeout) => {
  let jwtToken;
  _.forEach(requestData, (event) => {
    jwtToken = event.jwtToken || jwtToken;
    delete event.jwtToken;
  });

  const request = {
    url: '/event',
    method: 'post',
    data: requestData,
    timeout: timeout || EnvConfig.axios.defaultTimeout,
    headers: {
      'Content-Type': 'application/json',
      ...(jwtToken && { Authorization: jwtToken }),
    },
  };
  const response = await axios.request(request);
  return response;
};

/**
 * In addition to submitting the card data, provides standard handling of the response and closing the card with localized content
 * @param {Object} submitData
 * @returns {Promise<AxiosPromise | void>}
 */
export const SubmitLocalizedCard = async (submitData) => {
  const {
    requestData,
    brand = 'expedia',
    flowType,
    channelType,
    isError = false,
    translatedStrings,
    errMessageOnSubmitFailure = {
      heading: formatText(
        'error.TechnicalProblemOccurred.heading',
        translatedStrings,
      ),
      message: formatText(
        'error.TechnicalProblemOccurred.message',
        translatedStrings,
      ),
    },
  } = submitData;
  const isChatFlow = flowType === flowTypes.CHAT;
  const isVacFlow = channelType === channelTypes.VAC;
  const buttonLabel = formatText('error.Button', translatedStrings);
  const technicalErrorMsg = {
    heading: formatText(
      'error.TechnicalProblemOccurred.heading',
      translatedStrings,
    ),
    message: formatText(
      'error.TechnicalProblemOccurred.message',
      translatedStrings,
    ),
  };
  const title = getTitleLabelByTemplateId(requestData.templateId);
  const localizedTitle = formatText(title, translatedStrings);

  // errMessageOnSubmitFailure can be one we localize or
  // We get already localized from S3 BUCKET for payment widget
  // So we don't want to format text for already localized strings.
  let errorMessage;
  if (
    typeof errMessageOnSubmitFailure === 'string' &&
    errMessageOnSubmitFailure.indexOf('error.') >= 0
  ) {
    errorMessage = formatText(errMessageOnSubmitFailure, translatedStrings);
  } else {
    errorMessage = errMessageOnSubmitFailure;
  }

  return SubmitData(requestData)
    .then(() => {
      if (isError) {
        errorHtmlFromTemplate(
          errorMessage,
          requestData.templateId,
          brand,
          isChatFlow,
          isVacFlow,
          buttonLabel,
          localizedTitle,
        );
      } else {
        const cardCloseErrorMessage = {
          heading: formatText(
            'error.CardUnableClose.heading',
            translatedStrings,
          ),
          message: formatText(
            'error.CardUnableClose.message',
            translatedStrings,
          ),
        };
        CloseWebView(brand, cardCloseErrorMessage);
      }
    })
    .catch((err) => {
      let errorMsg = technicalErrorMsg;
      if (err.response && err.response.data) {
        errorMsg = err.response.data.customErrorMessage || errorMsg;
      }
      errorHtmlFromTemplate(
        errorMsg,
        requestData.templateId,
        brand,
        isChatFlow,
        isVacFlow,
        buttonLabel,
      );
    });
};
